import { FaCheck, FaDownload, FaEye, FaFileAlt, FaFolder, FaLock, FaShareAlt, FaTrashAlt, FaUserLock } from 'react-icons/fa'
import { MdPlaylistAdd } from 'react-icons/md'
import { BeatLoader } from 'react-spinners'
import { formatFileDate, formatFileSize } from '../../../utils/fileBrowserApi'

export const VisibilityBadge = ({ visibility }) => (
  <span
    className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
      visibility === 'public'
        ? 'bg-green-100 text-green-700'
        : 'bg-amber-100 text-amber-700'
    }`}
  >
    {visibility}
  </span>
)

export default function FileList({
  loading,
  folders,
  files,
  selectedKeys,
  onToggleSelect,
  onToggleSelectAll,
  onOpenFolder,
  onView,
  onDownload,
  onShare,
  onAddToQueue,
  onDeleteFile,
  onDeleteFolder,
  onManageAccess,
  isQueued,
  downloadingKey,
  viewingKey,
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <BeatLoader color={'#2b364b'} />
      </div>
    )
  }

  if (!folders.length && !files.length) {
    return (
      <div className="flex justify-center items-center mt-8">
        <p className="admin-dashboard text-2xl text-gray-500">
          This folder is empty.
        </p>
      </div>
    )
  }

  const allSelected = files.length > 0 && files.every(f => selectedKeys.has(f.key))

  return (
    <div className="overflow-x-auto">
      <table
        className="min-w-full overflow-hidden select-none"
        style={{
          border: '1px solid #999',
          borderRadius: '10px',
          borderSpacing: '0',
          tableLayout: 'fixed',
        }}
      >
        <colgroup>
          <col style={{ width: '44px' }} />
          <col style={{ width: '36%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '20%' }} />
          <col style={{ width: '12%' }} />
          <col style={{ width: '22%' }} />
        </colgroup>
        <thead className="thead">
          <tr className="table-head">
            <th
              scope="col"
              align="center"
              className="px-3 py-2"
              style={{ borderBottom: '1px solid #999' }}
            >
              <input
                type="checkbox"
                title="Select all (Ctrl+A)"
                className="cursor-pointer accent-[#2b364b]"
                checked={allSelected}
                disabled={!files.length}
                onChange={onToggleSelectAll}
              />
            </th>
            {['Name', 'Size', 'Last Modified', 'Visibility', 'Actions'].map(label => (
              <th
                key={label}
                scope="col"
                align="left"
                className="px-3 py-2"
                style={{ borderBottom: '1px solid #999' }}
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {folders.map(folder => (
            <tr
              key={folder.prefix}
              className="cursor-pointer hover:bg-blue-50 transition-colors"
              onClick={() => onOpenFolder(folder.prefix)}
            >
              <td align="center" className="px-3 py-3" style={{ borderBottom: '1px solid #999' }} />
              <td
                align="left"
                className="px-3 py-3 whitespace-nowrap table-order !text-[#000] !font-normal"
                style={{ borderBottom: '1px solid #999' }}
              >
                <span className="flex items-center gap-2 truncate">
                  <FaFolder className="text-[#8ba3c7] shrink-0" size={16} />
                  <span className="truncate font-medium" title={folder.name}>
                    {folder.name}
                  </span>
                </span>
              </td>
              <td className="px-3 py-3 table-order !text-[#000] !font-normal" style={{ borderBottom: '1px solid #999' }}>
                —
              </td>
              <td className="px-3 py-3 table-order !text-[#000] !font-normal" style={{ borderBottom: '1px solid #999' }}>
                —
              </td>
              <td className="px-3 py-3" style={{ borderBottom: '1px solid #999' }} />
              <td className="px-3 py-3" style={{ borderBottom: '1px solid #999' }}>
                <div className="flex items-center gap-3">
                  {onManageAccess && (
                    <button
                      type="button"
                      title="Manage access"
                      className="text-gray-500 hover:text-[#2b364b] bg-transparent border-none cursor-pointer p-0"
                      onClick={event => {
                        event.stopPropagation()
                        onManageAccess(folder)
                      }}
                    >
                      <FaUserLock size={15} />
                    </button>
                  )}
                  {onDeleteFolder && (
                    <button
                      type="button"
                      title="Delete folder (must be empty)"
                      className="text-gray-500 hover:text-red-600 bg-transparent border-none cursor-pointer p-0"
                      onClick={event => {
                        event.stopPropagation()
                        onDeleteFolder(folder)
                      }}
                    >
                      <FaTrashAlt size={14} />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}

          {files.map(file => {
            const queued = isQueued(file)
            return (
              <tr
                key={file.key}
                className="hover:bg-blue-50 transition-colors"
                onClick={event => {
                  // Explorer-style: shift+click extends the range, ctrl/cmd+click toggles.
                  if (event.shiftKey || event.ctrlKey || event.metaKey) {
                    onToggleSelect(file, event.shiftKey)
                  }
                }}
              >
                <td align="center" className="px-3 py-3" style={{ borderBottom: '1px solid #999' }}>
                  <input
                    type="checkbox"
                    className="cursor-pointer accent-[#2b364b]"
                    checked={selectedKeys.has(file.key)}
                    onClick={event => event.stopPropagation()}
                    onChange={event => onToggleSelect(file, event.nativeEvent.shiftKey)}
                  />
                </td>
                <td
                  align="left"
                  className="px-3 py-3 whitespace-nowrap table-order !text-[#000] !font-normal"
                  style={{ borderBottom: '1px solid #999' }}
                >
                  <span
                    className="flex items-center gap-2 truncate cursor-pointer hover:underline"
                    title={file.key}
                    onClick={event => {
                      // Let modifier-clicks bubble to the row's selection handler.
                      if (event.shiftKey || event.ctrlKey || event.metaKey) return
                      onView(file)
                    }}
                  >
                    <FaFileAlt className="text-blue-500 shrink-0" size={15} />
                    <span className="truncate">{file.name}</span>
                    {file.readOnly && (
                      <FaLock className="text-gray-400 shrink-0" size={11} title="Read-only" />
                    )}
                  </span>
                </td>
                <td
                  className="px-3 py-3 whitespace-nowrap table-order !text-[#000] !font-normal"
                  style={{ borderBottom: '1px solid #999' }}
                >
                  {formatFileSize(file.size)}
                </td>
                <td
                  className="px-3 py-3 whitespace-nowrap table-order !text-[#000] !font-normal"
                  style={{ borderBottom: '1px solid #999' }}
                >
                  {formatFileDate(file.lastModified)}
                </td>
                <td className="px-3 py-3" style={{ borderBottom: '1px solid #999' }}>
                  <VisibilityBadge visibility={file.visibility} />
                </td>
                <td className="px-3 py-3" style={{ borderBottom: '1px solid #999' }}>
                  <div className="flex items-center gap-3">
                    {viewingKey === file.key ? (
                      <BeatLoader color="#2b364b" size={5} />
                    ) : (
                      <button
                        type="button"
                        title="View"
                        className="text-gray-600 hover:text-[#2b364b] bg-transparent border-none cursor-pointer p-0"
                        onClick={() => onView(file)}
                      >
                        <FaEye size={16} />
                      </button>
                    )}
                    {downloadingKey === file.key ? (
                      <BeatLoader color="#2b364b" size={5} />
                    ) : (
                      <button
                        type="button"
                        title="Download"
                        className="text-gray-600 hover:text-[#2b364b] bg-transparent border-none cursor-pointer p-0"
                        onClick={() => onDownload(file)}
                      >
                        <FaDownload size={15} />
                      </button>
                    )}
                    {onShare && (
                      <button
                        type="button"
                        title="Share via email"
                        className="text-gray-600 hover:text-[#2b364b] bg-transparent border-none cursor-pointer p-0"
                        onClick={() => onShare(file)}
                      >
                        <FaShareAlt size={15} />
                      </button>
                    )}
                    <button
                      type="button"
                      title={queued ? 'Already in download queue' : 'Add to download queue'}
                      disabled={queued}
                      className={`bg-transparent border-none p-0 ${
                        queued
                          ? 'text-green-600 cursor-default'
                          : 'text-gray-600 hover:text-[#2b364b] cursor-pointer'
                      }`}
                      onClick={() => !queued && onAddToQueue(file)}
                    >
                      {queued ? <FaCheck size={15} /> : <MdPlaylistAdd size={20} />}
                    </button>
                    {onDeleteFile && !file.readOnly && (
                      <button
                        type="button"
                        title="Delete file"
                        className="text-gray-500 hover:text-red-600 bg-transparent border-none cursor-pointer p-0"
                        onClick={() => onDeleteFile(file)}
                      >
                        <FaTrashAlt size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
